import React, { useState, useRef, useEffect } from 'react';

interface TagSelectorProps {
  selectedTags: string[];
  onChange: (tags: string[]) => void;
  predefinedTags?: string[];
  placeholder?: string;
  className?: string;
}

const DEFAULT_PREDEFINED_TAGS = [
  '编程', 'AI', '代码', '写作', '工作',
  '学习', '生活', '创意', '营销', '商务',
  '邮件', '文档', '翻译', '总结', '分析',
  '教育', '科技', '设计', '产品', '运营'
];

const TagSelector: React.FC<TagSelectorProps> = ({
  selectedTags,
  onChange,
  predefinedTags = DEFAULT_PREDEFINED_TAGS,
  placeholder = '选择或输入标签...',
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 过滤出可用的预定义标签（未选中的）
  const availablePredefinedTags = predefinedTags.filter(tag => 
    !selectedTags.includes(tag) && 
    tag.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // 处理点击外部关闭下拉
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
        setInputValue('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 添加标签
  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !selectedTags.includes(trimmedTag)) {
      onChange([...selectedTags, trimmedTag]);
    }
    setInputValue('');
    setSearchTerm('');
  };

  // 移除标签
  const removeTag = (tagToRemove: string) => {
    onChange(selectedTags.filter(tag => tag !== tagToRemove));
  };

  // 处理输入框变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setInputValue(value);
    setSearchTerm(value);
    setIsOpen(true);
  };

  // 处理键盘事件
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'Backspace' && !inputValue && selectedTags.length > 0) {
      // 如果输入框为空且有选中的标签，删除最后一个标签
      removeTag(selectedTags[selectedTags.length - 1]);
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearchTerm('');
      setInputValue('');
    }
  };

  // 处理预定义标签点击
  const handlePredefinedTagClick = (tag: string) => {
    addTag(tag);
    inputRef.current?.focus();
  };

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      {/* 主输入区域 */}
      <div 
        className={`h-[48px] w-full px-4 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm cursor-text transition-all duration-200 flex items-center ${
          isOpen ? 'ring-2 ring-blue-500 border-transparent' : 'hover:border-gray-300 dark:hover:border-gray-500'
        }`}
        onClick={() => {
          setIsOpen(true);
          inputRef.current?.focus();
        }}
      >
        <div className="flex flex-wrap gap-1 items-center w-full">
          {/* 已选标签 */}
          {selectedTags.map((tag, index) => (
            <span
              key={index}
              className="inline-flex items-center px-2 py-1 bg-blue-100 dark:bg-blue-800 text-blue-800 dark:text-blue-200 text-sm rounded-md"
            >
              {tag}
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeTag(tag);
                }}
                className="ml-1 hover:text-blue-600 dark:hover:text-blue-300 focus:outline-none"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </span>
          ))}
          
          {/* 输入框 */}
          <input
            ref={inputRef}
            type="text"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(true)}
            placeholder={selectedTags.length === 0 ? placeholder : ''}
            className="flex-1 min-w-0 outline-none bg-transparent text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 text-sm"
          />
        </div>
      </div>

      {/* 下拉选项 */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg max-h-48 overflow-y-auto">
          {/* 输入提示 */}
          {inputValue.trim() && !availablePredefinedTags.includes(inputValue.trim()) && (
            <div
              onClick={() => handlePredefinedTagClick(inputValue)}
              className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer border-b border-gray-100 dark:border-gray-600"
            >
              <div className="flex items-center">
                <svg className="w-4 h-4 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                <span className="text-gray-700 dark:text-gray-300">
                  添加 "<span className="font-medium text-blue-600 dark:text-blue-400">{inputValue.trim()}</span>"
                </span>
              </div>
            </div>
          )}

          {/* 预定义标签 */}
          {availablePredefinedTags.length > 0 && (
            <>
              {inputValue.trim() && !availablePredefinedTags.includes(inputValue.trim()) && (
                <div className="px-3 py-1 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800 border-b border-gray-100 dark:border-gray-600">
                  推荐标签
                </div>
              )}
              {availablePredefinedTags.map((tag, index) => (
                <div
                  key={index}
                  onClick={() => handlePredefinedTagClick(tag)}
                  className="px-3 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer flex items-center"
                >
                  <svg className="w-4 h-4 text-gray-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                  </svg>
                  <span className="text-gray-700 dark:text-gray-300">{tag}</span>
                </div>
              ))}
            </>
          )}

          {/* 无选项提示 */}
          {availablePredefinedTags.length === 0 && !inputValue.trim() && (
            <div className="px-3 py-2 text-gray-500 dark:text-gray-400 text-sm">
              输入标签名称或从推荐中选择
            </div>
          )}
        </div>
      )}

    </div>
  );
};

export default TagSelector;
